import { cx } from "@light-technology/components";
import { api } from "../../state/api";
import { StatusType } from "../../state/api.types";
import { compactFormatter } from "../../utilities/analyticsHelpers";
import { HoverCard } from "../shared/HoverCard";
import { SkeletonLine } from "../shared/SkeletonLine";
import {
  areFiltersEqual,
  EMPTY_ACCOUNT_FILTERS,
  toAccountFilterParams,
  type AccountFilters,
} from "./accountFilterHelpers";

type Metric = {
  id: string;
  label: string;
  labelSingular: string;
  /** The filters clicking this box applies, and that its count is taken from. */
  filters: AccountFilters;
};

const ACTIVE: Metric = {
  id: "active",
  label: "active accounts",
  labelSingular: "active account",
  filters: { ...EMPTY_ACCOUNT_FILTERS, statuses: [StatusType.Active] },
};

const ACCOUNTS_NEED_ATTENTION: Metric = {
  id: "accounts-need-attention",
  label: "accounts need attention",
  labelSingular: "account needs attention",
  filters: { ...EMPTY_ACCOUNT_FILTERS, statuses: [StatusType.NeedsAttention] },
};

// Any device attached, healthy or not, so the device-issues box is a subset of
// this one rather than a separate population.
const ACCOUNTS_WITH_DEVICES: Metric = {
  id: "accounts-with-devices",
  label: "accounts with devices",
  labelSingular: "account with devices",
  filters: {
    ...EMPTY_ACCOUNT_FILTERS,
    deviceStatuses: ["connected", "disconnected"],
  },
};

const ACCOUNTS_WITH_DEVICE_ISSUES: Metric = {
  id: "accounts-with-device-issues",
  label: "accounts with device issues",
  labelSingular: "account with device issues",
  filters: { ...EMPTY_ACCOUNT_FILTERS, deviceStatuses: ["disconnected"] },
};

const EXPIRING: Metric = {
  id: "expiring",
  label: "upcoming expirations",
  labelSingular: "upcoming expiration",
  filters: { ...EMPTY_ACCOUNT_FILTERS, upcomingExpirationsOnly: true },
};

/**
 * Counts accounts matching a metric, ignoring the current search and filters: a
 * metric is the size of the whole queue, not of what is on screen. Undefined
 * while loading, which is not the same as a known zero.
 */
function useMetricCount(appUUID: string, metric: Metric): number | undefined {
  const query = api.useGetAccountsCountQuery({
    appUUID,
    search: "",
    ...toAccountFilterParams(metric.filters),
  });
  return query.data?.count;
}

type MetricBoxProps = {
  metric: Metric;
  count: number | undefined;
  isSelected: boolean;
  onSelect: () => void;
};

const MetricBox = ({ metric, count, isSelected, onSelect }: MetricBoxProps) => {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={onSelect}
      data-ph-capture-attribute-action={`account-metric-${metric.id}`}
      className="h-full rounded-xl text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
    >
      <HoverCard
        wrapperClassName="h-full"
        className={cx(
          "h-full w-full flex-col gap-1 p-3 @page-sm:p-6",
          isSelected && "border-brand bg-secondary",
        )}
      >
        <div className="font-mono text-display-sm font-semibold @page-sm:text-display-lg">
          {count === undefined ? (
            <SkeletonLine className="w-20" subtle />
          ) : (
            compactFormatter.format(count)
          )}
        </div>
        <div className="text-sm text-secondary">
          {count === 1 ? metric.labelSingular : metric.label}
        </div>
      </HoverCard>
    </button>
  );
};

type AccountMetricsProps = {
  appUUID: string;
  filters: AccountFilters;
  onSelect: (filters: AccountFilters) => void;
};

export const AccountMetrics = ({
  appUUID,
  filters,
  onSelect,
}: AccountMetricsProps) => {
  const active = useMetricCount(appUUID, ACTIVE);
  const accountsNeedAttention = useMetricCount(
    appUUID,
    ACCOUNTS_NEED_ATTENTION,
  );
  const accountsWithDevices = useMetricCount(appUUID, ACCOUNTS_WITH_DEVICES);
  const accountsWithDeviceIssues = useMetricCount(
    appUUID,
    ACCOUNTS_WITH_DEVICE_ISSUES,
  );
  const expiring = useMetricCount(appUUID, EXPIRING);

  // An empty queue is not worth a box; give the slot to the fleet instead.
  const showAccountsWithDevices =
    accountsNeedAttention === 0 && (accountsWithDevices ?? 0) > 0;

  const shown: { metric: Metric; count: number | undefined }[] = [
    { metric: ACTIVE, count: active },
    showAccountsWithDevices
      ? { metric: ACCOUNTS_WITH_DEVICES, count: accountsWithDevices }
      : { metric: ACCOUNTS_NEED_ATTENTION, count: accountsNeedAttention },
    { metric: ACCOUNTS_WITH_DEVICE_ISSUES, count: accountsWithDeviceIssues },
    { metric: EXPIRING, count: expiring },
  ];

  return (
    <div className="mb-3xl grid grid-cols-2 items-stretch gap-lg @page-sm:grid-cols-4">
      {shown.map(({ metric, count }) => {
        const isSelected = areFiltersEqual(filters, metric.filters);
        return (
          <MetricBox
            key={metric.id}
            metric={metric}
            count={count}
            isSelected={isSelected}
            onSelect={() => {
              onSelect(isSelected ? EMPTY_ACCOUNT_FILTERS : metric.filters);
            }}
          />
        );
      })}
    </div>
  );
};
